// src/components/layout/MobileMenu.tsx
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '../../stores/useAuthStore';
import { usePaymentStore } from '../../stores/usePaymentStore';
import { useThemeStore } from '../../stores/useThemeStore';
import { useLanguageStore } from '../../stores/useLanguageStore';
import { useThemePalette } from '../../hooks/useThemePalette';
import { useT } from '../../data/translations';
import { useSubscription } from '../../hooks/useSubscription';
import { handleLogout } from '../../services/authService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileMenu({ isOpen, onClose }: Props) {
  const navigate = useNavigate();
  const C        = useThemePalette();
  const t        = useT();
  const isDark   = useThemeStore((s) => s.theme) === 'dark';
  const toggleTheme = useThemeStore((s) => s.toggle);
  const { lang, toggle: toggleLang } = useLanguageStore();

  const user             = useAuthStore((s) => s.user);
  const profile          = useAuthStore((s) => s.profile);
  const isExerciseActive = useAuthStore((s) => s.isExerciseActive);
  const isLoggingOut     = useAuthStore((s) => s.isLoggingOut);
  const openAuthModal    = useAuthStore((s) => s.openAuthModal);
  const openProfileModal = useAuthStore((s) => s.openProfileModal);
  const openPaymentModal = usePaymentStore((s) => s.openModal);
  const { isSubscribed } = useSubscription();

  useEffect(() => {
    if (!isOpen) { document.body.style.overflow = ''; return; }
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', onKeyDown); };
  }, [isOpen, onClose]);

  const handleLink        = (path: string) => { onClose(); navigate(path); };
  const handlePremium     = () => { onClose(); openPaymentModal(); };
  const handleLogin       = () => { onClose(); openAuthModal('login'); };
  const handleSignup      = () => { onClose(); openAuthModal('signup'); };
  const handleProfile     = () => { onClose(); openProfileModal(); };
  const handleLogoutClick = async () => {
    onClose();
    try { await handleLogout(); } catch (e) { console.warn('[MobileMenu] logout failed:', e); }
  };

  const overlayStyle: React.CSSProperties = {
    position:      'fixed',
    inset:         0,
    background:    'rgba(0,0,0,0.7)',
    backdropFilter:'blur(4px)',
    zIndex:        60,
    opacity:       isOpen ? 1 : 0,
    pointerEvents: isOpen ? 'auto' : 'none',
    transition:    'opacity 0.3s ease',
  };

  const panelStyle: React.CSSProperties = {
    position:      'fixed',
    top:           0,
    left:          0,
    bottom:        0,
    width:         'min(80vw, 300px)',
    background:    isDark ? 'rgba(13,18,32,0.98)' : 'rgba(255,255,255,0.99)',
    backdropFilter:'blur(24px)',
    borderRight:   `1px solid ${C.border}`,
    zIndex:        61,
    transform:     isOpen ? 'translateX(0)' : 'translateX(-100%)',
    transition:    'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
    display:       'flex',
    flexDirection: 'column',
    overflow:      'hidden',
    boxShadow:     isOpen ? '20px 0 60px rgba(0,0,0,0.4)' : 'none',
  };

  const navLinkStyle: React.CSSProperties = {
    display:        'flex',
    alignItems:     'center',
    padding:        '14px 20px',
    fontSize:       '0.8rem',
    fontWeight:     700,
    letterSpacing:  '0.1em',
    textTransform:  'uppercase',
    color:          C.muted,
    background:     'transparent',
    border:         'none',
    borderRadius:   '10px',
    cursor:         'pointer',
    width:          '100%',
    textAlign:      'left',
    fontFamily:     'inherit',
    textDecoration: 'none',
    transition:     'color 0.15s ease, background 0.15s ease',
  };

  const ctaStyle = (variant: 'ghost' | 'solid' | 'danger'): React.CSSProperties => ({
    padding:      '13px 20px',
    borderRadius: '12px',
    fontSize:     '0.82rem',
    fontWeight:   700,
    letterSpacing:'0.06em',
    textTransform:'uppercase',
    cursor:       'pointer',
    width:        '100%',
    fontFamily:   'inherit',
    border:
      variant === 'ghost'  ? `1px solid ${C.border}` :
      variant === 'danger' ? '1px solid rgba(248,113,113,0.2)' :
      'none',
    background:
      variant === 'solid'  ? `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)` :
      variant === 'danger' ? 'rgba(248,113,113,0.08)' :
      (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'),
    color:
      variant === 'solid'  ? '#fff' :
      variant === 'danger' ? C.error :
      C.text,
  });

  return (
    <>
      <div style={overlayStyle} onClick={onClose} aria-hidden="true" />
      <aside style={panelStyle} role="dialog" aria-modal="true" aria-label="Mobile navigation menu">

        {/* Header */}
        <div style={{
          padding:        '24px 24px 20px',
          borderBottom:   `1px solid ${C.border}`,
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
        }}>
          <span style={{
            fontSize:             '16px',
            fontWeight:           800,
            letterSpacing:        '0.1em',
            textTransform:        'uppercase',
            background:           `linear-gradient(135deg, ${C.text} 0%, ${C.accent} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor:  'transparent',
            backgroundClip:       'text',
          }}>
            START
          </span>
          <button
            onClick={onClose}
            aria-label="Close menu"
            style={{
              background:     isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
              border:         `1px solid ${C.border}`,
              borderRadius:   '8px',
              color:          C.muted,
              width:          '32px',
              height:         '32px',
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              cursor:         'pointer',
              fontSize:       '16px',
              fontFamily:     'inherit',
            }}
          >
            ✕
          </button>
        </div>

        {/* Nav links */}
        <nav style={{ flex: 1, overflow: 'auto', padding: '16px 12px' }}>
          {([
            { label: t('NAV_BOOKS'),    path: '/'          },
            { label: t('NAV_ABOUT'),    path: '/about'     },
            { label: t('NAV_PARTNERS'), path: '/partners'  },
            { label: t('NAV_CONTACT'),  path: '/contact'   },
          ] as const).map(({ label, path }) => (
            <button key={path} style={navLinkStyle} onClick={() => handleLink(path as string)}>
              {label}
            </button>
          ))}
          {!isSubscribed && (
            <button style={{ ...navLinkStyle, color: C.premium }} onClick={handlePremium}>
              {t('NAV_PREMIUM')}
            </button>
          )}

          {/* Divider */}
          <div style={{ height: '1px', background: C.border, margin: '12px 8px' }} />

          {/* Theme + Language row */}
          <div style={{ display: 'flex', gap: '8px', padding: '4px 8px' }}>
            <button
              onClick={toggleTheme}
              style={{
                flex:          1,
                display:       'flex',
                alignItems:    'center',
                justifyContent:'center',
                gap:           '7px',
                padding:       '10px',
                borderRadius:  '10px',
                background:    isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                border:        `1px solid ${C.border}`,
                color:         C.muted,
                fontSize:      '0.72rem',
                fontWeight:    700,
                cursor:        'pointer',
                fontFamily:    'inherit',
                letterSpacing: '0.06em',
              }}
            >
              {isDark
                ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              }
              {isDark ? 'Light' : 'Dark'}
            </button>

            <button
              onClick={toggleLang}
              style={{
                flex:          1,
                display:       'flex',
                alignItems:    'center',
                justifyContent:'center',
                gap:           '7px',
                padding:       '10px',
                borderRadius:  '10px',
                background:    isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                border:        `1px solid ${C.border}`,
                color:         C.muted,
                fontSize:      '0.72rem',
                fontWeight:    700,
                cursor:        'pointer',
                fontFamily:    'inherit',
                letterSpacing: '0.06em',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
              {lang === 'en' ? 'GEO' : 'ENG'}
            </button>
          </div>
        </nav>

        {/* Auth section */}
        <div style={{
          padding:       '20px 16px 28px',
          borderTop:     `1px solid ${C.border}`,
          display:       'flex',
          flexDirection: 'column',
          gap:           '10px',
        }}>
          {!user ? (
            <>
              <button style={ctaStyle('ghost')} onClick={handleLogin}>{t('NAV_LOGIN')}</button>
              <button style={ctaStyle('solid')} onClick={handleSignup}>{t('NAV_SIGNUP')}</button>
            </>
          ) : (
            <>
              <div style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 4px 16px', borderBottom:`1px solid ${C.border}` }}>
                <div style={{
                  width:'40px', height:'40px', borderRadius:'50%', flexShrink:0, overflow:'hidden',
                  background: profile?.avatarUrl ? 'transparent' : `linear-gradient(135deg, ${C.accent}, ${C.accent2})`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  color:'#fff', fontWeight:700, fontSize:'1rem',
                  boxShadow:`0 0 0 2px rgba(79,140,247,0.25)`,
                }}>
                  {profile?.avatarUrl
                    ? <img src={profile.avatarUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    : (profile?.displayName || user.email || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ fontSize:'0.9rem', fontWeight:600, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {profile?.displayName || user.email || 'User'}
                </div>
              </div>

              <button style={ctaStyle('ghost')} onClick={handleProfile}>My Profile</button>
              <button
                style={{
                  ...ctaStyle('danger'),
                  opacity: isExerciseActive || isLoggingOut ? 0.5 : 1,
                  cursor:  isExerciseActive || isLoggingOut ? 'not-allowed' : 'pointer',
                }}
                onClick={handleLogoutClick}
                disabled={isExerciseActive || isLoggingOut}
              >
                {isLoggingOut ? 'Logging out…' : 'Logout'}
              </button>
            </>
          )}
        </div>
      </aside>
    </>
  );
}